import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { toast } from 'sonner';

import {
    BillingGroup,
    BillingGroupRun,
    CustodyType,
    DateStr,
    FeeType,
    GUID,
    ManualFeeAmendmentRequest,
    Tenant,
} from '@ndvr/billing-api';

import { pluralize } from '@/utils/string-utils';
import { BillingApi, billingUrl } from 'API';
import { formatLocaleDate } from 'utils';
import { useSelectedTenantGUIDs } from 'views/auth/tenant-selection';
import { Toast } from 'widgets';

const BillingRunQueryKeys = {
    all: ['billingGroupRuns'] as const,
    byDate: (tenantGUIDs: GUID<Tenant>[], billingDate: DateStr, feeType?: FeeType, estimated = false) =>
        [
            'billingGroupRuns',
            estimated ? 'estimated' : 'generated',
            [...tenantGUIDs].sort(),
            billingDate,
            feeType ?? null,
        ] as const,
    billingDates: (tenantGUIDs: GUID<Tenant>[]) =>
        ['billingGroupRuns', 'billingDates', [...tenantGUIDs].sort()] as const,
    estimateBillingDates: (tenantGUIDs: GUID<Tenant>[]) =>
        ['billingGroupRuns', 'estimateBillingDates', [...tenantGUIDs].sort()] as const,
} as const;

export const useBillingGroupRuns = (billingDate: DateStr, feeType?: FeeType, estimated = false) => {
    const tenantGUIDs = useSelectedTenantGUIDs();

    return useQuery({
        queryKey: BillingRunQueryKeys.byDate(tenantGUIDs, billingDate, feeType, estimated),
        enabled: tenantGUIDs.length > 0,
        staleTime: estimated ? 5 * 60 * 1000 : undefined,
        queryFn: async (): Promise<BillingGroupRun[]> => {
            const api = BillingApi();
            const [response] = estimated
                ? await api.getEstimatedBillingGroupRuns(tenantGUIDs, { billingDate, feeType })
                : await api.getBillingGroupRuns(tenantGUIDs, { billingDate, feeType });
            return response ?? [];
        },
    });
};

export const useApproveBillingGroupRuns = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (billingGroupRunGUIDs: GUID<BillingGroupRun>[]) =>
            BillingApi({ hideError: true }).approveBillingGroupRuns(billingGroupRunGUIDs),
        onSuccess: (_data, billingGroupRunGUIDs) => {
            toast.success(`${pluralize(billingGroupRunGUIDs.length, 'Billing group run')} approved`);
            return queryClient.invalidateQueries({ queryKey: BillingRunQueryKeys.all });
        },
        onError: (error) => Toast.forError(error, 'Failed to approve billing group runs'),
    });
};

export type RerunBillingGroupsRequest = {
    billingDate: DateStr;
    billingGroupGUIDs: GUID<BillingGroup>[];
};

export const useRerunBillingGroups = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ billingDate, billingGroupGUIDs }: RerunBillingGroupsRequest) => {
            const [skippedGroups] = await BillingApi({ hideError: true }).rerunBillingGroups(billingGroupGUIDs, {
                billingDate,
            });
            return skippedGroups ?? [];
        },
        onSuccess: (skippedGroups, { billingGroupGUIDs }) => {
            const rerunCount = billingGroupGUIDs.length - skippedGroups.length;
            if (rerunCount > 0) {
                toast.success(`${pluralize(rerunCount, 'Billing group')} rerun`);
            }
            return queryClient.invalidateQueries({ queryKey: BillingRunQueryKeys.all });
        },
        onError: (error) => Toast.forError(error, 'Failed to rerun billing groups'),
    });
};

export const useGenerateBillingRunReport = () =>
    useMutation({
        mutationFn: (billingGroupRunGUID: GUID<BillingGroupRun>) =>
            BillingApi().generateReportForBillingRun(billingGroupRunGUID),
    });

export const useDownloadOutstandingFeeReport = () => {
    const tenantGUIDs = useSelectedTenantGUIDs();

    return useMutation({
        mutationFn: (custodyType: CustodyType) =>
            BillingApi().generateOutstandingFeeReport(tenantGUIDs, { custodyType }),
    });
};

export const useApplyManualFeeAmendment = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            billingGroupRunGUID,
            request,
        }: {
            billingGroupRunGUID: GUID<BillingGroupRun>;
            request: ManualFeeAmendmentRequest;
        }) => BillingApi().applyManualFeeAmendment(billingGroupRunGUID, request),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: BillingRunQueryKeys.all });
        },
    });
};

// Unlike useGenerateBillingRunReport, this needs the Blob itself (to preview, not download),
// so it can't go through the generated client — that always triggers a browser download.
export const useBillingRunReportPreview = () =>
    useMutation({
        mutationFn: async (billingGroupRunGUID: GUID<BillingGroupRun>) => {
            const response = await fetch(
                `${billingUrl}/reporting/claro-client-billing-report/billing-run/${billingGroupRunGUID}`,
            );
            if (!response.ok) throw new Error('Failed to generate invoice preview');
            return response.blob();
        },
    });

// Billing dates are always the 1st of a month. `formatLocaleDate` emits the trailing-Z form
// (`2026-07-01Z`) the services use for LocalDate — the endpoint rejects a bare `2026-07-01`.
const firstOfMonth = (monthOffset: number) => {
    const today = new Date();
    return formatLocaleDate(new Date(today.getFullYear(), today.getMonth() + monthOffset, 1));
};

export type BillingDateOption = {
    date: DateStr;
    isCurrent: boolean;
    isUpcoming: boolean;
};

export const useBillingDateOptions = () => {
    const tenantGUIDs = useSelectedTenantGUIDs();

    const { data: availableDates = [], isLoading: availableDatesLoading } = useQuery({
        queryKey: BillingRunQueryKeys.billingDates(tenantGUIDs),
        enabled: tenantGUIDs.length > 0,
        queryFn: async (): Promise<DateStr[]> => {
            const [response] = await BillingApi().getAvailableBillingDates(tenantGUIDs);
            return response ?? [];
        },
    });
    const { data: estimateDates = [], isLoading: estimateDatesLoading } = useQuery({
        queryKey: BillingRunQueryKeys.estimateBillingDates(tenantGUIDs),
        enabled: tenantGUIDs.length > 0,
        queryFn: async (): Promise<DateStr[]> => {
            const [response] = await BillingApi().getEstimateBillingDates(tenantGUIDs);
            return response ?? [];
        },
    });

    return useMemo(() => {
        const currentDate = firstOfMonth(0);
        const estimateDateSet = new Set(estimateDates);
        const options: BillingDateOption[] = [...new Set([...estimateDates, currentDate, ...availableDates])]
            .sort((a, b) => b.localeCompare(a))
            .map((date) => ({
                date,
                isCurrent: date === currentDate,
                isUpcoming: estimateDateSet.has(date),
            }));

        return {
            options,
            defaultDate: currentDate,
            isLoading: availableDatesLoading || estimateDatesLoading,
        };
    }, [availableDates, availableDatesLoading, estimateDates, estimateDatesLoading]);
};
